import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { TEMPORARY_STORAGE_COLORS_PALETTE } from 'util/ConstantValues';
import { getBrightnessFromColor } from 'util/commons';
import { CustomCheckBox } from 'components/atoms/CustomCheckbox';
import TableFilterCalendar from 'components/molecules/TableFilterCalendar';
import TableFilterMultiSelect from 'components/molecules/TableFilterMultiSelect';
import { CustomColumn } from 'components/molecules/ResponsiveHistoryTable';
import {
  Option,
  StorageShipmentRow,
  ShipmentRegistrationFilter,
} from 'util/Types';
import styles from './ShipmentRegistration.module.scss';
import Popover from 'components/atoms/Popover';

type Params = {
  checkedIds: string[];
  convertedStorageRows: StorageShipmentRow[];
  filter: ShipmentRegistrationFilter;
  storageMaterialNameOptions: Option[];
  selectedMaterialName: string | null;
  onSelectAll: () => void;
  onCheckItem: (id: string) => void;
  onFilterChange: (value: ShipmentRegistrationFilter) => void;
};

export const useStorageColumns = ({
  checkedIds,
  convertedStorageRows,
  filter,
  storageMaterialNameOptions,
  selectedMaterialName,
  onSelectAll,
  onCheckItem,
  onFilterChange,
}: Params): CustomColumn<StorageShipmentRow>[] => {
  const { t } = useTranslation();

  return useMemo(
    () => [
      {
        Header: (() => {
          const hasMixedMaterials =
            selectedMaterialName === null &&
            new Set(convertedStorageRows.map((r) => r.materialName)).size > 1;

          if (hasMixedMaterials) return <></>;

          const selectableCount = selectedMaterialName
            ? convertedStorageRows.filter(
                (r) => r.materialName === selectedMaterialName
              ).length
            : convertedStorageRows.length;

          return (
            <span
              onClick={onSelectAll}
              style={{
                cursor:
                  convertedStorageRows.length > 0 ? 'pointer' : 'not-allowed',
                color: convertedStorageRows.length > 0 ? 'inherit' : 'gray',
              }}
            >
              {t(
                selectableCount === checkedIds.length && selectableCount > 0
                  ? 'common.deselect_all'
                  : 'common.select_all'
              )}
            </span>
          );
        })(),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        accessor: 'checkbox' as any,
        className: styles.checkboxColumn,
        Cell: ({ row }) => {
          const isDisabled =
            selectedMaterialName !== null &&
            row.original.materialName !== selectedMaterialName;

          if (isDisabled) return null;

          return (
            <CustomCheckBox
              id={row.original.processingObjectId}
              darkMode
              checked={checkedIds.includes(row.original.processingObjectId)}
              onChange={() => onCheckItem(row.original.processingObjectId)}
            />
          );
        },
      },
      {
        Header: (
          <TableFilterCalendar
            label={
              t(
                'compressions.shipment.shipment_registration.columns.storage_datetime'
              ) || ''
            }
            value={filter?.datetime}
            onChange={(value) => onFilterChange({ ...filter, datetime: value })}
            labelClassName={styles.tableFilterLabel}
            endDateRequired
          />
        ),
        accessor: 'datetime',
        Cell: ({ value }) => <>{value}</>,
      },
      {
        Header:
          t(
            'compressions.shipment.shipment_registration.columns.resource_id'
          ) || '',
        accessor: 'processingObjectId',
        Cell: ({ value }) => <>{value}</>,
      },
      {
        Header:
          t(
            'compressions.shipment.shipment_registration.columns.person_in_charge'
          ) || '',
        accessor: 'user',
        Cell: ({ value }) => <>{value}</>,
      },
      {
        Header: (
          <TableFilterMultiSelect
            label={
              t(
                'compressions.shipment.shipment_registration.columns.material_name'
              ) || ''
            }
            options={storageMaterialNameOptions}
            values={filter?.materialName}
            onChange={(value) =>
              onFilterChange({ ...filter, materialName: value })
            }
            classNames={{ label: styles.tableFilterLabel }}
          />
        ),
        accessor: 'materialName',
        Cell: ({ value }) => <>{value}</>,
      },
      {
        Header:
          t('compressions.shipment.shipment_registration.columns.weight') || '',
        accessor: 'weight',
        Cell: ({ value }) => <>{`${value}kg`}</>,
      },
      {
        Header:
          t(
            'compressions.shipment.shipment_registration.columns.local_storage'
          ) || '',
        accessor: 'localStorage',
        Cell: ({ row }) => {
          const loc = row.original.localStorage;

          if (!loc) {
            return <>{t('storage_location_registration.not_set')}</>;
          }

          const bgColor =
            TEMPORARY_STORAGE_COLORS_PALETTE[
              Number(loc.color) % TEMPORARY_STORAGE_COLORS_PALETTE.length
            ];

          return (
            <span
              className={styles.storageLocationBadge}
              style={{
                backgroundColor: bgColor,
                color:
                  getBrightnessFromColor(bgColor) <= 100
                    ? '#ffffff'
                    : '#1b1b1b',
              }}
            >
              <Popover
                autoGen={{
                  lines: 1,
                  odd: 4,
                  wrapperStyle: {
                    width: 'fit-content',
                  },
                }}
                popUpStyle={{
                  fontSize: 18,
                }}
                positions={['top']}
                content={loc.name}
                containerStyle={{
                  maxWidth: '400px',
                  wordBreak: 'break-all',
                  zIndex: '1000',
                }}
              >
                <span>{loc.name}</span>
              </Popover>
            </span>
          );
        },
      },
    ],
    [
      t,
      checkedIds,
      convertedStorageRows,
      filter,
      storageMaterialNameOptions,
      selectedMaterialName,
    ]
  );
};
